// src/components/templates/CreateTemplateModal.tsx
import React, { useState } from "react";

interface CreateTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (templateData: {
    name: string;
    document_type: string;
    description: string;
  }) => Promise<{ success: boolean; error?: string }>;
  loading?: boolean;
}

export const CreateTemplateModal: React.FC<CreateTemplateModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  loading = false,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    document_type: "",
    description: "",
  });
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validação dos campos obrigatórios
    if (!formData.name.trim() || !formData.document_type.trim() || !formData.description.trim()) {
      setError("Todos os campos são obrigatórios");
      return;
    }

    const result = await onCreate(formData);
    
    if (result.success) {
      // Limpa o formulário e fecha o modal
      setFormData({ name: "", document_type: "", description: "" });
      onClose();
    } else {
      setError(result.error || "Erro ao criar template");
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpa erro quando usuário começa a digitar
    if (error) setError(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-card rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Cabeçalho */}
        <div className="p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            Novo Template
          </h2>
          <p className="text-muted-foreground text-sm mt-1">
            Preencha os dados para criar um novo template de documento
          </p>
        </div>

        {/* Formulário */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-destructive text-destructive-foreground p-3 rounded-md text-sm">
              {error}
            </div>
          )}

          {/* Nome do Template */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-foreground mb-2">
              Nome do Template *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Ex: Contrato de Prestação de Serviços"
              disabled={loading}
            />
          </div>

          {/* Tipo do Documento */}
          <div>
            <label htmlFor="document_type" className="block text-sm font-medium text-foreground mb-2">
              Tipo do Documento *
            </label>
            <select
              id="document_type"
              value={formData.document_type}
              onChange={(e) => handleChange("document_type", e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={loading}
            >
              <option value="">Selecione um tipo</option>
              <option value="Contrato">Contrato</option>
              <option value="Petição">Petição</option>
              <option value="Procuração">Procuração</option>
              <option value="Termo">Termo</option>
              <option value="Declaração">Declaração</option>
              <option value="Outro">Outro</option>
            </select>
          </div>

          {/* Prompt/Descrição */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-foreground mb-2">
              Prompt *
            </label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
              placeholder="Descreva o prompt ou instruções para gerar este documento..."
              disabled={loading}
            />
          </div>

          {/* Rodapé com ações */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-muted-foreground bg-transparent border border-border rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-border transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-md hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors disabled:opacity-50"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-2"></div>
                  Criando...
                </div>
              ) : (
                "Criar Template"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};